import { useState } from "react";
import { CheckCircle, Circle, PaperPlaneTilt, PlugsConnected, X } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";

export default function TwilioCard({ sms, whatsapp, canManage, onChanged }) {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ account_sid: "", auth_token: "", sender_sms: "", sender_whatsapp: "" });

  const channels = [sms?.attivo && "SMS", whatsapp?.attivo && "WhatsApp"].filter(Boolean);
  const connected = channels.length > 0;

  const connect = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await api.post("/integrations/twilio/connect", {
        account_sid: form.account_sid.trim(),
        auth_token: form.auth_token.trim(),
        sender_sms: form.sender_sms.trim() || null,
        sender_whatsapp: form.sender_whatsapp.trim() || null,
      });
      toast.success(`Account Twilio collegato: ${r.data.channels.join(" + ")} attivi`);
      setOpen(false);
      setForm({ account_sid: "", auth_token: "", sender_sms: "", sender_whatsapp: "" });
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const testSend = async (channel) => {
    setBusy(true);
    try {
      const r = await api.post("/integrations/twilio/test-send", { channel });
      toast.success(r.data.mock
        ? `Invio di prova SIMULATO (${channel}) verso ${r.data.to}`
        : `Messaggio di prova inviato via ${channel} a ${r.data.to}`);
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const disconnect = async () => {
    if (!window.confirm("Scollegare l'account Twilio? SMS e WhatsApp torneranno al canale piattaforma.")) return;
    setBusy(true);
    try {
      await api.post("/integrations/twilio/disconnect");
      toast.success("Account Twilio scollegato");
      onChanged();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white border border-zinc-200 rounded-lg p-4" data-testid="twilio-card">
      <div className="flex items-start gap-3">
        <span className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${
          connected ? "bg-emerald-50 text-emerald-600" : "bg-zinc-100 text-zinc-400"}`}>
          <PlugsConnected size={20} weight="bold" />
        </span>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display font-bold text-zinc-900">SMS / WhatsApp con il tuo account Twilio</span>
            {connected ? (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-emerald-100 text-emerald-800 border-emerald-300 flex items-center gap-1"
                data-testid="twilio-status">
                <CheckCircle size={10} weight="fill" /> {channels.join(" + ").toUpperCase()} ATTIVI
              </span>
            ) : (
              <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border bg-zinc-100 text-zinc-500 border-zinc-300 flex items-center gap-1"
                data-testid="twilio-status">
                <Circle size={10} /> NON COLLEGATO
              </span>
            )}
          </div>
          <p className="text-xs text-zinc-500 mt-1">
            Invio SMS e WhatsApp con il tuo account Twilio (credenziali verificate sulle API Twilio e salvate cifrate).
            Alternativa al collegamento diretto Meta. Finché non colleghi un account, gli invii restano simulati (MOCK).
          </p>
        </div>
      </div>

      {canManage && (
        <div className="mt-3 space-y-2">
          {!connected && !open && (
            <button onClick={() => setOpen(true)} data-testid="twilio-open"
              className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5">
              <PlugsConnected size={15} weight="bold" /> Configura account Twilio
            </button>
          )}
          {open && (
            <form onSubmit={connect} className="bg-zinc-50 border border-zinc-200 rounded-lg p-3.5 space-y-2" data-testid="twilio-form">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <input required value={form.account_sid} placeholder="Account SID (AC…)" data-testid="twilio-sid"
                  onChange={(e) => setForm({ ...form, account_sid: e.target.value })}
                  className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
                <input required type="password" value={form.auth_token} placeholder="Auth Token" data-testid="twilio-token"
                  onChange={(e) => setForm({ ...form, auth_token: e.target.value })}
                  className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
                <input value={form.sender_sms} placeholder="Mittente SMS (es. +15551234567)" data-testid="twilio-sms-sender"
                  onChange={(e) => setForm({ ...form, sender_sms: e.target.value })}
                  className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
                <input value={form.sender_whatsapp} placeholder="Mittente WhatsApp (es. +14155238886)" data-testid="twilio-wa-sender"
                  onChange={(e) => setForm({ ...form, sender_whatsapp: e.target.value })}
                  className="h-10 px-3 rounded-md border border-zinc-300 text-sm bg-white" />
              </div>
              <p className="text-[11px] text-zinc-400">Le credenziali vengono verificate subito sulle API Twilio; se non valide il collegamento viene rifiutato.</p>
              <div className="flex gap-2">
                <button disabled={busy} data-testid="twilio-connect"
                  className="h-9 px-4 rounded-md bg-emerald-600 text-white text-xs font-bold disabled:opacity-40">
                  {busy ? "Verifica in corso…" : "Verifica e collega"}
                </button>
                <button type="button" onClick={() => setOpen(false)} data-testid="twilio-cancel"
                  className="h-9 px-3 rounded-md border border-zinc-300 text-xs font-semibold text-zinc-600 flex items-center gap-1">
                  <X size={13} /> Annulla
                </button>
              </div>
            </form>
          )}
          {connected && (
            <div className="flex gap-2 flex-wrap">
              {sms?.attivo && (
                <button onClick={() => testSend("sms")} disabled={busy} data-testid="twilio-test-sms"
                  className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40">
                  <PaperPlaneTilt size={15} weight="bold" /> Prova SMS
                </button>
              )}
              {whatsapp?.attivo && (
                <button onClick={() => testSend("whatsapp")} disabled={busy} data-testid="twilio-test-whatsapp"
                  className="h-9 px-4 rounded-md bg-zinc-900 text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-40">
                  <PaperPlaneTilt size={15} weight="bold" /> Prova WhatsApp
                </button>
              )}
              <button onClick={disconnect} disabled={busy} data-testid="twilio-disconnect"
                className="h-9 px-3 rounded-md border border-rose-300 text-rose-600 text-xs font-bold disabled:opacity-40">
                Scollega
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
